/**
 * What each civilisation builds with in each age. Settlement Age camps are wicker, logs, canvas or
 * bamboo; the Feudal Age brings wattle and daub, heavier timber and thatch; the Castle Age is stone
 * in each civilisation's own castle tradition; the Imperial Age carved wood, marble and gilding; and
 * the Industrial Age steel, concrete and brick.
 */
export type Mat =
  | 'wicker'
  | 'log'
  | 'canvas'
  | 'bamboo'
  | 'daub'
  | 'plank'
  | 'woodGrass'
  | 'bambooGrass'
  | 'stone'
  | 'carved'
  | 'marble'
  | 'steel'
  | 'concrete'
  | 'brick';

/** How a civilisation shapes stone in the Castle Age (and keeps shaping its castles after it). */
export type Flavour = 'british' | 'french' | 'german' | 'iberian' | 'ottoman' | 'japanese' | 'chinese' | 'steppe' | 'indian' | 'aztec' | 'egyptian';

interface CivStyle {
  mats: [Mat, Mat, Mat, Mat, Mat];
  flavour: Flavour;
  /** Castle Age stone colour. */
  stone: number;
  /** Colour used for carved / lacquered Imperial woodwork. */
  carved?: number;
}

const STYLES: Record<string, CivStyle> = {
  english: { mats: ['wicker', 'daub', 'stone', 'marble', 'brick'], flavour: 'british', stone: 0x8e8c86 },
  french: { mats: ['log', 'daub', 'stone', 'marble', 'steel'], flavour: 'french', stone: 0xcfc4aa },
  germans: { mats: ['log', 'plank', 'stone', 'carved', 'brick'], flavour: 'german', stone: 0x7a7670, carved: 0x4a3222 },
  spanish: { mats: ['wicker', 'daub', 'stone', 'marble', 'concrete'], flavour: 'iberian', stone: 0xc8a878 },
  portuguese: { mats: ['wicker', 'daub', 'stone', 'marble', 'brick'], flavour: 'iberian', stone: 0xd6c6a2 },
  ottomans: { mats: ['canvas', 'woodGrass', 'stone', 'marble', 'concrete'], flavour: 'ottoman', stone: 0xd8cfb8 },
  japanese: { mats: ['bamboo', 'bambooGrass', 'stone', 'carved', 'steel'], flavour: 'japanese', stone: 0x8a8a84, carved: 0x3a2a24 },
  chinese: { mats: ['bamboo', 'bambooGrass', 'stone', 'carved', 'brick'], flavour: 'chinese', stone: 0x7c7e80, carved: 0x9a2a20 },
  mongols: { mats: ['canvas', 'plank', 'stone', 'carved', 'steel'], flavour: 'steppe', stone: 0x9a8e7a, carved: 0xa8502a },
  indians: { mats: ['bamboo', 'bambooGrass', 'stone', 'marble', 'brick'], flavour: 'indian', stone: 0xb06a4a },
  aztecs: { mats: ['wicker', 'woodGrass', 'stone', 'marble', 'concrete'], flavour: 'aztec', stone: 0x8a9282 },
  egyptians: { mats: ['canvas', 'woodGrass', 'stone', 'marble', 'concrete'], flavour: 'egyptian', stone: 0xd2b480 },
};

export function civStyle(civKey: string): CivStyle {
  return STYLES[civKey] ?? STYLES.english;
}

export function matFor(civKey: string, age: number): Mat {
  return civStyle(civKey).mats[Math.max(0, Math.min(4, age))];
}

/** Light, hand-built materials of the first two ages. */
export const isPrimitive = (m: Mat) => m === 'wicker' || m === 'log' || m === 'canvas' || m === 'bamboo';
/** Materials roofed with straw, reed or grass. */
export const isThatched = (m: Mat) => m === 'wicker' || m === 'bamboo' || m === 'woodGrass' || m === 'bambooGrass';
/** Industrial materials that use window bands rather than framed windows. */
export const isModern = (m: Mat) => m === 'steel' || m === 'concrete';
